import type { TicketDTO, TicketStatus } from "@zplanem/responses"
import type { Locale, Translations } from "@zplanem/translations"
import { webPaths } from "@zplanem/web-paths"
import type { DragEvent, ReactNode } from "react"
import { Link } from "react-router-dom"
import { formatFullDateTime, getDisplayName } from "../../../lib/user-display"
import { cn } from "../../../lib/utils"

type TicketCopy = Translations["tickets"]

const STATUS_TONE: Record<TicketStatus, string> = {
	open: "border-ring/40 bg-primary/10 text-primary dark:bg-ring/15 dark:text-ring",
	in_progress:
		"border-warning/40 bg-warning-muted text-warning-muted-foreground",
	done: "border-success/40 bg-success-muted text-success-muted-foreground",
	cancelled: "border-border bg-pending text-pending-foreground",
}

const KIND_TONE: Record<TicketDTO["kind"], string> = {
	task: "border-border bg-muted text-foreground",
	defect: "border-destructive bg-destructive text-destructive-foreground",
}

export interface TicketReturnTo {
	path: string
	label: string
}

export function TicketStatusBadge({
	status,
	t,
}: {
	status: TicketStatus
	t: TicketCopy
}) {
	return (
		<span
			className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-medium ${STATUS_TONE[status]}`}
		>
			{t.status[status]}
		</span>
	)
}

export function TicketKindBadge({
	kind,
	t,
}: {
	kind: TicketDTO["kind"]
	t: TicketCopy
}) {
	return (
		<span
			className={cn(
				"inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-semibold",
				KIND_TONE[kind],
			)}
		>
			{t.kind[kind]}
		</span>
	)
}

export function TicketCard({
	ticket,
	t,
	locale,
	topicLabel,
	projectLabel,
	projectHref,
	returnTo,
	statusControl,
	draggable,
	dragging,
	onDragStart,
	onDragEnd,
}: {
	ticket: TicketDTO
	t: TicketCopy
	locale: Locale
	topicLabel?: string | null
	projectLabel?: string | null
	projectHref?: string | null
	returnTo?: TicketReturnTo
	statusControl?: ReactNode
	draggable?: boolean
	dragging?: boolean
	onDragStart?: (e: DragEvent<HTMLElement>) => void
	onDragEnd?: (e: DragEvent<HTMLElement>) => void
}) {
	return (
		<article
			draggable={draggable}
			onDragStart={onDragStart}
			onDragEnd={onDragEnd}
			className={`rounded-lg border border-border bg-card px-3 py-3 text-sm shadow-sm transition-colors hover:border-foreground/25 ${
				draggable ? "cursor-grab active:cursor-grabbing" : ""
			} ${dragging ? "opacity-60" : ""}`}
		>
			<div className="flex items-start justify-between gap-2">
				<Link
					to={webPaths.ticket(ticket.propertyId, ticket.id)}
					state={returnTo ? { returnTo } : undefined}
					className="min-w-0 flex-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
				>
					<div className="font-semibold leading-snug text-foreground">
						{ticket.title}
					</div>
					{ticket.description ? (
						<p className="mt-1 text-xs text-muted-foreground line-clamp-2">
							{ticket.description}
						</p>
					) : null}
				</Link>
				<div className="flex shrink-0 flex-col items-end gap-2">
					<TicketKindBadge kind={ticket.kind} t={t} />
					{statusControl}
				</div>
			</div>
			<div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
				{projectLabel ? (
					projectHref ? (
						<Link
							to={projectHref}
							className="rounded-sm border border-border bg-background px-2 py-0.5 text-foreground hover:border-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
						>
							{projectLabel}
						</Link>
					) : (
						<span className="rounded-sm border border-border bg-background px-2 py-0.5 text-foreground">
							{projectLabel}
						</span>
					)
				) : null}
				{topicLabel ? (
					<span className="rounded-sm border border-border bg-muted px-2 py-0.5 text-foreground">
						{topicLabel}
					</span>
				) : null}
				<span>
					{ticket.assignee ? getDisplayName(ticket.assignee) : t.unassigned}
				</span>
				{ticket.dueAt ? (
					<span>
						{t.dueLabel}: {formatFullDateTime(ticket.dueAt, locale)}
					</span>
				) : null}
			</div>
		</article>
	)
}

export function TicketSkeletonCards() {
	return (
		<div className="space-y-3">
			{["ticket-skeleton-1", "ticket-skeleton-2", "ticket-skeleton-3"].map(
				(key) => (
					<div
						key={key}
						className="animate-pulse rounded-lg border border-border bg-card px-3 py-3"
					>
						<div className="h-4 w-2/3 rounded bg-muted" />
						<div className="mt-2 h-3 w-full rounded bg-muted" />
						<div className="mt-3 h-3 w-1/2 rounded bg-muted" />
					</div>
				),
			)}
		</div>
	)
}
